import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useGetCurrentUser } from '@/entities/authentication/api/generated/api';
import type { ReleaseFlags } from '@/entities/session/model/releaseFlag';
import { useSessionStore } from '@/entities/session/model/sessionStore';
import { Spinner, useSkeletonMinDisplay } from '../../shared/ui';

type AuthProviderProps = {
  children: ReactNode;
};

/**
 * 認証プロバイダー（薄いアダプター）
 *
 * App層の責務:
 * - Cookie監視による認証状態の自動更新
 * - entities/session ストアとの同期
 * - ユーザー情報の取得とキャッシュ
 *
 * 重要: useCookiesフックを使用してCookieの変更を検知し、自動的に再レンダリングする
 */
export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [cookies] = useCookies(['auth_token']);
  const { setAuthenticated, setUser, setReleaseFlags } = useSessionStore();
  const [releaseFlagsLoaded, setReleaseFlagsLoaded] = useState(false);

  // Cookieの存在を確認して認証状態を判定
  const isAuthenticated = Boolean(cookies.auth_token);

  // 認証状態をストアに同期
  useEffect(() => {
    setAuthenticated(isAuthenticated);
  }, [isAuthenticated, setAuthenticated]);

  // ログイン済みの場合のみユーザー情報を取得
  const {
    data: currentUser,
    isLoading: isLoadingUser,
    isError,
  } = useGetCurrentUser({
    query: {
      enabled: isAuthenticated,
      staleTime: 1000 * 60 * 60 * 1, // 1時間キャッシュ
    },
  });

  // ユーザー情報をストアに同期
  useEffect(() => {
    if (currentUser) {
      setUser({
        id: currentUser.id,
        name: currentUser.name,
        email: currentUser.email,
        lastLogin: currentUser.last_login ?? null,
        isFirstLoginCompleted: currentUser.is_first_login_completed,
        role: currentUser.role ?? null,
        tenant: {
          id: currentUser.tenant.id,
          code: currentUser.tenant.code,
          name: currentUser.tenant.name,
          localGovernmentCode: currentUser.tenant.local_government_code,
        },
      });
    }
  }, [currentUser, setUser]);

  // Release Flags をストアに同期
  useEffect(() => {
    if (currentUser) {
      const releaseFlags = Object.fromEntries(
        (currentUser.release_flags ?? []).map((flag) => [flag.type, flag.is_enabled]),
      ) as ReleaseFlags;
      setReleaseFlags(releaseFlags);
      setReleaseFlagsLoaded(true);
    }
  }, [currentUser, setReleaseFlags]);

  const loading = useSkeletonMinDisplay(isLoadingUser);

  // tanstack router初期化前のため
  if (isError && window.location.pathname !== '/404') {
    window.location.replace('/404');
  }

  // release_flags がストアにセットされるまでローディングを続ける
  // useEffect は render 後に実行されるため、children（Router）の beforeLoad が
  // release_flags セット前に走るレースコンディションを防ぐ

  if (loading || (isAuthenticated && !releaseFlagsLoaded)) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }
  return <>{children}</>;
};
